import { useEffect, useMemo, useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { AnimeCard } from "@/components/AnimeCard";
import { api } from "@/lib/api";

export const Catalog = ({ initialQuery = "", title = "Catalog", showHeader = true }) => {
  const [list, setList] = useState([]);
  const [genres, setGenres] = useState([]);
  const [q, setQ] = useState(initialQuery);
  const [genre, setGenre] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => setQ(initialQuery), [initialQuery]);

  useEffect(() => {
    api.get("/genres").then((r) => setGenres(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/anime", { params: { q: q || undefined, genre: genre || undefined } })
        .then((r) => setList(r.data))
        .finally(() => setLoading(false));
    }, 180);
    return () => clearTimeout(t);
  }, [q, genre]);

  const count = useMemo(() => list.length, [list]);

  return (
    <section data-testid="catalog-section" className="max-w-[1400px] mx-auto px-5 sm:px-8">
      {showHeader && (
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
          <div>
            <span className="kp-eyebrow">Library</span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mt-3 tracking-tight">
              {title} <span className="text-[#64748B] text-xl font-mono align-top">/{count}</span>
            </h2>
          </div>
          <div className="flex items-center gap-2 border border-white/10 px-4 h-12 w-full lg:w-80 focus-within:border-[#FF2A55] transition-colors">
            <Search size={16} className="text-[#64748B]" />
            <input
              data-testid="anime-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title..."
              className="bg-transparent outline-none text-sm w-full placeholder:text-[#64748B]"
            />
          </div>
        </div>
      )}

      <div data-testid="genre-filter-select" className="flex flex-wrap items-center gap-2 mb-10">
        <SlidersHorizontal size={14} className="text-[#64748B] mr-1" />
        <button data-testid="genre-pill-all" className="kp-pill" data-active={genre === ""} onClick={() => setGenre("")}>
          All
        </button>
        {genres.map((g) => (
          <button key={g} data-testid={`genre-pill-${g.toLowerCase().replace(/\s+/g, "-")}`} className="kp-pill" data-active={genre === g} onClick={() => setGenre(genre === g ? "" : g)}>
            {g}
          </button>
        ))}
      </div>

      {loading && list.length === 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] bg-[#0F0D16] animate-pulse kp-card-clip" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <div data-testid="catalog-empty" className="py-24 text-center border border-dashed border-white/10">
          <p className="font-display text-lg">Nothing found</p>
          <p className="text-[#64748B] text-sm mt-2">Try a different search or filter.</p>
        </div>
      ) : (
        <div data-testid="anime-grid" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6">
          {list.map((a, i) => (
            <AnimeCard key={a.id} anime={a} index={i} />
          ))}
        </div>
      )}
    </section>
  );
};
